import React, { useState, useEffect } from 'react';
import { Search, Plus, Filter, Mail, MessageSquare, Send, Phone, Video, MoreHorizontal, Paperclip, Smile, User, Star, MapPin, Calendar, Clock, FileText, Download, Reply, Forward, Archive, Trash2, Flag } from 'lucide-react';

const CommunicationsCenter = () => {
  const [selectedConversation, setSelectedConversation] = useState(1);
  const [selectedChannel, setSelectedChannel] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [messageInput, setMessageInput] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [showTemplateModal, setShowTemplateModal] = useState(false);

  // Mock data pour les conversations
  const conversations = [
    {
      id: 1,
      client: {
        name: "Sophie Martin",
        avatar: "SM",
        status: "online",
        vipLevel: "Platinum",
        location: "Villa Azure, Cannes",
        checkin: "2024-09-15",
        checkout: "2024-09-22",
        email: "sophie.martin@email.com",
        phone: "+33 6 12 34 56 78"
      },
      channel: "WhatsApp",
      lastMessage: "Merci pour les recommandations de restaurants !",
      timestamp: "14:30",
      unread: 0,
      messages: [
        {
          id: 1,
          sender: "client",
          content: "Bonjour, pouvez-vous me recommander de bons restaurants à Cannes ?",
          timestamp: "14:15",
          status: "read"
        },
        {
          id: 2,
          sender: "agent",
          content: "Bonjour Sophie ! Je serais ravi de vous recommander quelques excellents restaurants. Pour du gastronomique, je vous conseille La Palme d'Or au Martinez, et pour une ambiance plus décontractée, Le Comptoir du Marché.",
          timestamp: "14:20",
          status: "read"
        },
        {
          id: 3,
          sender: "client",
          content: "Merci pour les recommandations de restaurants !",
          timestamp: "14:30",
          status: "read"
        }
      ]
    },
    {
      id: 2,
      client: {
        name: "Jean-Marc Dubois",
        avatar: "JD",
        status: "away",
        vipLevel: "Gold",
        location: "Château Provence",
        checkin: "2024-09-20",
        checkout: "2024-09-27",
        email: "jm.dubois@email.com",
        phone: "+33 6 98 76 54 32"
      },
      channel: "Email",
      lastMessage: "Demande de dégustation de vins privée",
      timestamp: "13:45",
      unread: 2,
      messages: [
        {
          id: 1,
          sender: "client",
          subject: "Demande de dégustation de vins privée",
          content: `Bonjour,

J'espère que vous allez bien. Je vous écris concernant notre séjour prévu du 20 au 27 septembre au Château Provence.

Nous aimerions organiser une dégustation de vins privée pour notre groupe de 8 personnes. Nous sommes particulièrement intéressés par :

• Les vins locaux de la région Provence
• Une sélection de grands crus français
• Un accord mets-vins avec des spécialités locales
• La présence d'un sommelier expert

Pourriez-vous nous proposer quelques options avec les tarifs correspondants ? Nous sommes flexibles sur la date et l'horaire pendant notre séjour.

Dans l'attente de votre retour,

Cordialement,
Jean-Marc Dubois`,
          timestamp: "13:45",
          status: "delivered",
          attachments: [
            { name: "preferences_vins.pdf", size: "245 KB", type: "pdf" },
            { name: "liste_invites.xlsx", size: "12 KB", type: "excel" }
          ]
        }
      ]
    },
    {
      id: 3,
      client: {
        name: "Isabella Romano",
        avatar: "IR",
        status: "offline",
        vipLevel: "Diamond",
        location: "Villa Toscane",
        checkin: "2024-09-25",
        checkout: "2024-10-02",
        email: "isabella.romano@email.com",
        phone: "+39 335 123 4567"
      },
      channel: "Email",
      lastMessage: "Informations transfert aéroport",
      timestamp: "12:20",
      unread: 1,
      messages: [
        {
          id: 1,
          sender: "client",
          subject: "Informations transfert aéroport",
          content: `Chère équipe PRIVIA,

J'espère que tout va bien de votre côté. Je vous contacte concernant notre réservation à la Villa Toscane du 25 septembre au 2 octobre.

Pourriez-vous me confirmer si le transfert depuis l'aéroport de Florence est inclus dans notre package ? Si ce n'est pas le cas, pourriez-vous m'indiquer les options disponibles et leurs tarifs ?

Nous arrivons le vol Lufthansa LH1234 à 14h30 le 25 septembre avec 4 personnes et 6 bagages.

Merci d'avance pour votre retour rapide.

Cordialement,
Isabella Romano`,
          timestamp: "12:20",
          status: "sent"
        }
      ]
    },
    {
      id: 4,
      client: {
        name: "Marcus Thompson",
        avatar: "MT",
        status: "online",
        vipLevel: "Black",
        location: "Yacht Serenity",
        checkin: "2024-09-18",
        checkout: "2024-09-25",
        email: "marcus.thompson@email.com",
        phone: "+1 555 123 4567"
      },
      channel: "WhatsApp",
      lastMessage: "Parfait, merci !",
      timestamp: "11:45",
      unread: 0,
      messages: [
        {
          id: 1,
          sender: "client",
          content: "Bonjour, pouvez-vous confirmer l'heure d'embarquement pour demain ?",
          timestamp: "11:30",
          status: "read"
        },
        {
          id: 2,
          sender: "agent",
          content: "Bonjour Marcus ! L'embarquement est prévu à 10h00 au port de Monaco. L'équipage vous attendra au ponton B.",
          timestamp: "11:40",
          status: "read"
        },
        {
          id: 3,
          sender: "client",
          content: "Parfait, merci !",
          timestamp: "11:45",
          status: "read"
        }
      ]
    }
  ];

  const currentConversation = conversations.find(conv => conv.id === selectedConversation);
  const isEmailConversation = currentConversation?.channel === 'Email';

  const filteredConversations = conversations.filter(conv => {
    const matchesSearch = conv.client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         conv.lastMessage.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesChannel = selectedChannel === 'all' || 
                           (selectedChannel === 'whatsapp' && conv.channel === 'WhatsApp') ||
                           (selectedChannel === 'email' && conv.channel === 'Email');
    return matchesSearch && matchesChannel;
  });

  const getChannelIcon = (channel) => {
    switch (channel) {
      case 'WhatsApp':
        return <MessageSquare className="w-4 h-4 text-green-600" />;
      case 'Email':
        return <Mail className="w-4 h-4 text-blue-600" />;
      default:
        return <MessageSquare className="w-4 h-4 text-gray-600" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'online':
        return 'bg-green-500';
      case 'away':
        return 'bg-yellow-500';
      case 'offline':
        return 'bg-gray-400';
      default:
        return 'bg-gray-400';
    }
  };

  const getVipColor = (level) => {
    switch (level) {
      case 'Black':
        return 'bg-gray-900 text-white';
      case 'Diamond':
        return 'bg-blue-600 text-white';
      case 'Platinum':
        return 'bg-gray-600 text-white';
      case 'Gold':
        return 'bg-yellow-600 text-white';
      default:
        return 'bg-gray-600 text-white';
    }
  };

  const renderWhatsAppMessage = (message, isLast) => (
    <div key={message.id} className={`flex ${message.sender === 'agent' ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
        message.sender === 'agent' 
          ? 'bg-yellow-600 text-white' 
          : 'bg-gray-100 text-gray-800'
      }`}>
        <p className="text-sm">{message.content}</p>
        <div className="flex items-center justify-between mt-1">
          <span className={`text-xs ${
            message.sender === 'agent' ? 'text-yellow-100' : 'text-gray-500'
          }`}>
            {message.timestamp}
          </span>
          {message.sender === 'agent' && (
            <span className="text-xs text-yellow-100">✓✓</span>
          )}
        </div>
      </div>
    </div>
  );

  const renderEmailMessage = (message) => (
    <div key={message.id} className="bg-white border border-gray-200 rounded-lg p-6 mb-4">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold ${
            message.sender === 'agent' ? 'bg-yellow-600' : 'bg-gray-600'
          }`}>
            {message.sender === 'agent' ? 'PR' : currentConversation.client.avatar}
          </div>
          <div>
            <div className="font-semibold text-gray-800">
              {message.sender === 'agent' ? 'PRIVIA Concierge' : currentConversation.client.name}
            </div>
            <div className="text-sm text-gray-600">
              {message.sender === 'agent' ? 'concierge@privia.com' : currentConversation.client.email}
            </div>
            <div className="text-xs text-gray-500">{message.timestamp}</div>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <button className="p-1 text-gray-400 hover:text-gray-600">
            <Reply className="w-4 h-4" />
          </button>
          <button className="p-1 text-gray-400 hover:text-gray-600">
            <Forward className="w-4 h-4" />
          </button>
          <button className="p-1 text-gray-400 hover:text-gray-600">
            <Flag className="w-4 h-4" />
          </button>
          <button className="p-1 text-gray-400 hover:text-gray-600">
            <MoreHorizontal className="w-4 h-4" />
          </button>
        </div>
      </div>

      {message.subject && (
        <h3 className="font-semibold text-lg text-gray-800 mb-3">
          {message.subject}
        </h3>
      )}

      <div className="prose prose-sm max-w-none text-gray-700 mb-4">
        <div style={{ whiteSpace: 'pre-line' }}>
          {message.content}
        </div>
      </div>

      {message.attachments && message.attachments.length > 0 && (
        <div className="border-t border-gray-200 pt-4">
          <div className="text-sm font-medium text-gray-700 mb-2">
            Pièces jointes ({message.attachments.length})
          </div>
          <div className="space-y-2">
            {message.attachments.map((attachment, index) => (
              <div key={index} className="flex items-center space-x-3 p-2 bg-gray-50 rounded-lg">
                <FileText className="w-5 h-5 text-gray-600" />
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-800">{attachment.name}</div>
                  <div className="text-xs text-gray-500">{attachment.size}</div>
                </div>
                <button className="p-1 text-gray-400 hover:text-gray-600">
                  <Download className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderWhatsAppInput = () => (
    <div className="bg-white border-t border-gray-200 p-4">
      <div className="flex items-end space-x-2">
        <div className="flex-1">
          <div className="flex items-center space-x-2 mb-2">
            <button 
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              onClick={() => setShowTemplateModal(true)}
            >
              Modèles
            </button>
          </div>
          <div className="flex items-end space-x-2">
            <div className="flex-1 relative">
              <textarea
                placeholder="Tapez votre message..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                rows="1"
                value={messageInput}
                onChange={(e) => setMessageInput(e.target.value)}
              />
            </div>
            <button className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg">
              <Paperclip className="w-4 h-4" />
            </button>
            <button className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg">
              <Smile className="w-4 h-4" />
            </button>
            <button className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700">
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderEmailInput = () => (
    <div className="bg-white border-t border-gray-200 p-6">
      <div className="space-y-4">
        <div className="flex items-center space-x-4">
          <button className="text-sm text-blue-600 hover:text-blue-700 font-medium">
            Modèles Email
          </button>
          <button className="text-sm text-gray-600 hover:text-gray-700">
            Brouillons
          </button>
        </div>
        
        <div>
          <input
            type="text"
            placeholder="Objet de l'email..."
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
            value={emailSubject}
            onChange={(e) => setEmailSubject(e.target.value)}
          />
        </div>

        <div>
          <textarea
            placeholder="Composez votre email..."
            className="w-full px-4 py-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
            rows="6"
            value={messageInput}
            onChange={(e) => setMessageInput(e.target.value)}
          />
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <button className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg">
              <Paperclip className="w-4 h-4" />
            </button>
            <button className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg">
              <FileText className="w-4 h-4" />
            </button>
            <span className="text-sm text-gray-500">Pièces jointes</span>
          </div>
          
          <div className="flex items-center space-x-2">
            <button className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
              Brouillon
            </button>
            <button className="px-6 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700">
              Envoyer
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Centre de Communications</h1>
            <p className="text-gray-600">Gérez vos conversations WhatsApp et emails</p>
          </div>
          <div className="flex items-center space-x-3">
            <button className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 flex items-center space-x-2">
              <Plus className="w-4 h-4" />
              <span>Nouvelle conversation</span>
            </button>
          </div>
        </div>
      </div>

      <div className="flex h-screen">
        {/* Sidebar - Liste des conversations */}
        <div className="w-80 bg-white border-r border-gray-200 flex flex-col">
          {/* Filtres et recherche */}
          <div className="p-4 border-b border-gray-200">
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Rechercher conversations..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            <div className="flex space-x-2">
              <button
                onClick={() => setSelectedChannel('all')}
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  selectedChannel === 'all' 
                    ? 'bg-yellow-100 text-yellow-800' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Tous
              </button>
              <button
                onClick={() => setSelectedChannel('whatsapp')}
                className={`px-3 py-1 rounded-full text-sm font-medium flex items-center space-x-1 ${
                  selectedChannel === 'whatsapp' 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <MessageSquare className="w-3 h-3" />
                <span>WhatsApp</span>
              </button>
              <button
                onClick={() => setSelectedChannel('email')}
                className={`px-3 py-1 rounded-full text-sm font-medium flex items-center space-x-1 ${
                  selectedChannel === 'email' 
                    ? 'bg-blue-100 text-blue-800' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <Mail className="w-3 h-3" />
                <span>Email</span>
              </button>
            </div>
          </div>

          {/* Liste des conversations */}
          <div className="flex-1 overflow-y-auto">
            {filteredConversations.map((conversation) => (
              <div
                key={conversation.id}
                onClick={() => setSelectedConversation(conversation.id)}
                className={`p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 ${
                  selectedConversation === conversation.id ? 'bg-yellow-50 border-l-4 border-l-yellow-600' : ''
                }`}
              >
                <div className="flex items-start space-x-3">
                  <div className="relative">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold ${getVipColor(conversation.client.vipLevel)}`}>
                      {conversation.client.avatar}
                    </div>
                    <div className={`absolute -bottom-1 -right-1 w-3 h-3 rounded-full border-2 border-white ${getStatusColor(conversation.client.status)}`}></div>
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-gray-800 truncate">{conversation.client.name}</h3>
                      <div className="flex items-center space-x-1">
                        {getChannelIcon(conversation.channel)}
                        <span className="text-xs text-gray-500">{conversation.timestamp}</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2 text-sm text-gray-600 mb-1">
                      <MapPin className="w-3 h-3" />
                      <span className="truncate">{conversation.client.location}</span>
                    </div>
                    
                    <p className="text-sm text-gray-600 truncate">{conversation.lastMessage}</p>
                    
                    {conversation.unread > 0 && (
                      <div className="flex items-center justify-between mt-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getVipColor(conversation.client.vipLevel)}`}>
                          {conversation.client.vipLevel}
                        </span>
                        <span className="bg-red-500 text-white text-xs rounded-full px-2 py-1">
                          {conversation.unread}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Zone de conversation principale */}
        {currentConversation ? (
          <div className="flex-1 flex flex-col">
            {/* Header de la conversation */}
            <div className="bg-white border-b border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="relative">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold ${getVipColor(currentConversation.client.vipLevel)}`}>
                      {currentConversation.client.avatar}
                    </div>
                    <div className={`absolute -bottom-1 -right-1 w-3 h-3 rounded-full border-2 border-white ${getStatusColor(currentConversation.client.status)}`}></div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800">{currentConversation.client.name}</h3>
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                      {getChannelIcon(currentConversation.channel)}
                      <span>{currentConversation.channel}</span>
                      <span>•</span>
                      <span className={currentConversation.client.status === 'online' ? 'text-green-600' : 'text-gray-500'}>
                        {currentConversation.client.status === 'online' ? 'En ligne' : 
                         currentConversation.client.status === 'away' ? 'Absent' : 'Hors ligne'}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {!isEmailConversation && (
                    <>
                      <button className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg">
                        <Phone className="w-4 h-4" />
                      </button>
                      <button className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg">
                        <Video className="w-4 h-4" />
                      </button>
                    </>
                  )}
                  {isEmailConversation && (
                    <>
                      <button className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg">
                        <Archive className="w-4 h-4" />
                      </button>
                      <button className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </>
                  )}
                  <button className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg">
                    <MoreHorizontal className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Messages */}
            <div className={`flex-1 overflow-y-auto ${isEmailConversation ? 'bg-gray-50 p-4' : 'p-4'} space-y-4`}>
              {currentConversation.messages.map((message, index) => 
                isEmailConversation 
                  ? renderEmailMessage(message)
                  : renderWhatsAppMessage(message, index === currentConversation.messages.length - 1)
              )}
            </div>

            {/* Zone de saisie */}
            {isEmailConversation ? renderEmailInput() : renderWhatsAppInput()}
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center bg-gray-50">
            <div className="text-center">
              <MessageSquare className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-800 mb-2">Sélectionnez une conversation</h3>
              <p className="text-gray-600">Choisissez une conversation pour commencer à communiquer</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CommunicationsCenter;